import { useCallback, useEffect, useMemo, useState } from "react";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useLiveRefreshFallback } from "@/shared/hooks/useLiveRefreshFallback";
import { getUploadMedicationMatches, type MedicationMatch } from "@/portals/patient/api/medicationMatchingApi";
import type { ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";

export interface UploadMedicationGroup {
  upload: ZipUploadEntry;
  medications: MedicationMatch[];
}

interface MedicationMatchingResult {
  /** The 2 most recently completed uploads, each with its own medications —
   *  empty until at least one real (non-sync) upload has finished. */
  groups: UploadMedicationGroup[];
  isLoading: boolean;
  error: string | null;
}

// Normalization typically finishes in low single-digit seconds (a handful
// of RxNav round trips at most, often zero on a cache hit) — short enough
// that a demo watching "Matching…" resolve live needs a much tighter poll
// than this app's usual 60s live-refresh fallback.
const POLL_INTERVAL_MS = 4000;

/**
 * Backs the "Medication Matching" page: the 2 most recently completed real
 * uploads (reads the list from the existing UploadHistoryContext, doesn't
 * re-fetch it), each with its own medications and RxNorm resolution status.
 *
 * Each upload's medications are fetched by THAT upload's own id, never
 * "whatever's most recent for the patient" — so this can never accidentally
 * only reflect the latest file's data, even though only 2 are shown.
 */
export function useMedicationMatching(): MedicationMatchingResult {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const { zipUploads, isLoading: isLoadingUploads } = useUploadHistory();

  const recentUploads = useMemo(
    () =>
      zipUploads
        .filter(
          (u): u is ZipUploadEntry & { uploadId: string } =>
            u.kind !== "sync" && u.status === "complete" && !!u.uploadId,
        )
        .slice(0, 2),
    [zipUploads],
  );

  const [medicationsByUploadId, setMedicationsByUploadId] = useState<Record<string, MedicationMatch[]>>({});
  const [isLoadingMedications, setIsLoadingMedications] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMedications = useCallback(() => {
    if (recentUploads.length === 0) {
      setIsLoadingMedications(false);
      return;
    }
    Promise.all(recentUploads.map((u) => getUploadMedicationMatches(u.uploadId, actingAsProfileId)))
      .then((results) => {
        const next: Record<string, MedicationMatch[]> = {};
        recentUploads.forEach((u, i) => {
          next[u.uploadId] = results[i];
        });
        setMedicationsByUploadId(next);
        setError(null);
      })
      .catch(() => setError("Couldn't load medication matching results. Please try again later."))
      .finally(() => setIsLoadingMedications(false));
  }, [recentUploads, actingAsProfileId]);

  useEffect(() => {
    setIsLoadingMedications(true);
    fetchMedications();
  }, [fetchMedications]);

  const hasPending = Object.values(medicationsByUploadId).some((meds) =>
    meds.some((m) => m.normalizationStatus === "pending"),
  );
  // Only actually re-fetches while something is still pending — a cheap
  // no-op otherwise, so this can run unconditionally on the usual interval
  // instead of needing its own start/stop wiring once everything resolves.
  const pollingRefresh = useCallback(() => {
    if (hasPending) fetchMedications();
  }, [hasPending, fetchMedications]);
  useLiveRefreshFallback(pollingRefresh, POLL_INTERVAL_MS);

  const groups: UploadMedicationGroup[] = recentUploads.map((upload) => ({
    upload,
    medications: medicationsByUploadId[upload.uploadId] ?? [],
  }));

  return { groups, isLoading: isLoadingUploads || isLoadingMedications, error };
}
